import { EditPencil } from "components/icons/EditPencil";
import { INCORPORATION_FORM_SOURCE } from "constants/incorporation";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useHistory, useParams } from "react-router-dom";
import {
  Stage,
  useDeleteFormTemplateMutation,
} from "store/apis/incorporationFormBuilder";
import { BackendError } from "types/utils/error";
import { DiscardTemplateModal } from "./DiscardTemplateModal";
import { EditTemplateNameModal } from "./EditTemplateNameModal";
import { IncorporationFormBuilder } from "./IncorporationFormBuilder";
import { IncorporationSaveStatus } from "./IncorporationSaveStatus";
import { IncorporationTrackerActionsMenu } from "./IncorporationTrackerActionsMenu";
import { RearrangeStepsModal } from "./RearrangeStepsModal";
import { ReviewEditorToggle } from "./ReviewEditorToggle";
import { useIncorporationFormTree } from "./useIncorporationFormTree";

const EMPTY_STAGES: Stage[] = [];

type RouteParams = {
  templateId: string;
};

export const IncorporationTemplateFormBuilder = () => {
  usePageTitle("Incorporation Template");
  const { alertToast } = useToast();
  const history = useHistory();
  const { templateId } = useParams<RouteParams>();
  const query = useQuery();
  const { update } = useUpdateQuery();
  const isEditOverride = query.get("edit") === "true";
  const editNameModal = useModal();
  const discardTemplateModal = useModal();
  const rearrangeStepsModal = useModal();

  const { data, isLoading, isSuccess } = useIncorporationFormTree({
    source: INCORPORATION_FORM_SOURCE.TEMPLATE,
    contentId: templateId,
  });

  const [deleteTemplate, { isLoading: isDiscarding }] =
    useDeleteFormTemplateMutation();

  const isEditable = isEditOverride;
  const title = data?.title || data?.display_title || "Untitled template";

  const onEditTemplateClick = () => {
    update({ query: "edit", value: "true" });
  };

  const onPreviewTemplateClick = () => {
    update({ query: "edit", value: null });
  };

  const onConfirmDiscardTemplate = async () => {
    try {
      await deleteTemplate({ templateId }).unwrap();
      discardTemplateModal.close();
      history.push("/admin/incorporation/templates");
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  const pageTitle = (
    <span className="t-inline-flex t-items-center t-gap-1.5 sm:t-inline">
      {title}
      <button
        type="button"
        onClick={editNameModal.open}
        className="all:unset t-flex t-cursor-pointer sm:t-ml-1.5 sm:t-inline-flex sm:t-align-middle t-text-text-30 hover:t-text-text-60 hover:t-bg-surface-lighter-grey"
      >
        <EditPencil className="t-size-4" />
      </button>
    </span>
  );

  const headerRight = (
    <div className="t-flex t-items-center t-gap-3">
      {isEditable && <IncorporationSaveStatus />}
      <ReviewEditorToggle
        isEditable={isEditable}
        isPublished={false}
        onPreview={onPreviewTemplateClick}
        onEdit={onEditTemplateClick}
      />
      <IncorporationTrackerActionsMenu
        isEditable={isEditable}
        isPublished={false}
        onRearrangeSteps={rearrangeStepsModal.open}
        onDiscardTemplate={discardTemplateModal.open}
      />
    </div>
  );

  return (
    <>
      <IncorporationFormBuilder
        source={INCORPORATION_FORM_SOURCE.TEMPLATE}
        contentId={templateId}
        data={data}
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEditable={isEditable}
        isAnswerable={false}
        pageTitle={pageTitle}
        breadcrumbName="Templates"
        breadcrumbLink="/admin/incorporation/templates"
        headerRight={isSuccess && headerRight}
      />
      <EditTemplateNameModal
        isOpen={editNameModal.isOpen}
        close={editNameModal.close}
        templateId={templateId}
        currentTitle={title}
      />
      <DiscardTemplateModal
        isOpen={discardTemplateModal.isOpen}
        close={discardTemplateModal.close}
        onDiscard={onConfirmDiscardTemplate}
        isLoading={isDiscarding}
      />
      <RearrangeStepsModal
        isOpen={rearrangeStepsModal.isOpen}
        close={rearrangeStepsModal.close}
        treeId={templateId}
        source={INCORPORATION_FORM_SOURCE.TEMPLATE}
        stages={data?.stages ?? EMPTY_STAGES}
      />
    </>
  );
};
